/**
 * City Agency Landing Pages - Wave 4
 * Adds: Johannesburg, Nairobi, Manila, Ho Chi Minh City, Bogota, Lima, Doha, Muscat, Accra, Tunis
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards. React, TypeScript, cloud-native.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containers, monitoring, managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA, DevOps integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, design systems.' },
  { name: 'Cybersecurity', description: 'Pen testing, security audits, SOC, compliance (ISO 27001, GDPR, HIPAA).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse systems.' },
];

export const CITY_AGENCY_PAGES_WAVE4: Record<string, CityAgencyData> = {

  'johannesburg': {
    slug: 'software-development-agency-johannesburg',
    cityName: 'Johannesburg',
    countryName: 'South Africa',
    flag: '🇿🇦',
    timezone: 'SAST (GMT+2)',
    currency: 'ZAR',
    title: {
      en: 'Software Development Agency Johannesburg | Call IT Dev',
      fr: 'Agence de Développement Logiciel Johannesburg | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات جوهانسبرغ | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Johannesburgo | Call IT Dev',
      de: 'Softwareentwicklungsagentur Johannesburg | Call IT Dev',
    },
    description: {
      en: 'Top software development agency in Johannesburg. React, AI, cloud — 60% savings vs local rates. Free consultation.',
      fr: 'Agence de développement logiciel à Johannesburg. React, IA, cloud — 60% d\'économies. Consultation gratuite.',
      ar: 'أفضل وكالة تطوير برمجيات في جوهانسبرغ. React، ذكاء اصطناعي، سحابة — وفر 60%. استشارة مجانية.',
      es: 'Agencia líder de desarrollo de software en Johannesburgo. React, IA, nube — 60% ahorro. Consulta gratis.',
      de: 'Top Softwareentwicklungsagentur in Johannesburg. React, KI, Cloud — 60% Ersparnis. Kostenlose Beratung.',
    },
    keywords: ['software development Johannesburg', 'web development Johannesburg', 'app development South Africa', 'React developers Johannesburg'],
    heroTitle: {
      en: 'Software Development Agency in Johannesburg',
      fr: 'Agence de Développement Logiciel à Johannesburg',
      ar: 'وكالة تطوير البرمجيات في جوهانسبرغ',
      es: 'Agencia de Desarrollo de Software en Johannesburgo',
      de: 'Softwareentwicklungsagentur in Johannesburg',
    },
    heroSubtitle: {
      en: 'Enterprise-grade software at nearshore prices. React, AI, cloud — 60% savings vs Joburg agencies.',
      fr: 'Logiciels d\'entreprise à prix nearshore. React, IA, cloud — 60% d\'économies vs agences locales.',
      ar: 'برمجيات مؤسسية بأسعار nearshore. React، ذكاء اصطناعي، سحابة — وفر 60%.',
      es: 'Software empresarial a precios nearshore. React, IA, nube — 60% ahorro vs agencias locales.',
      de: 'Enterprise-Software zu Nearshore-Preisen. React, KI, Cloud — 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Johannesburg Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Johannesburg Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات جوهانسبرغ Call IT Dev',
      es: 'Por qué las Empresas de Johannesburgo Eligen Call IT Dev',
      de: 'Warum Unternehmen aus Johannesburg Call IT Dev wählen',
    },
    whyReasons: [
      { icon: '💰', title: '60% Cost Savings', description: 'Compared to ZAR 800-1,500/h Johannesburg rates.' },
      { icon: '⏰', title: 'Same Timezone Overlap', description: 'GMT+2 — only 1 hour from CET.' },
      { icon: '🛡️', title: 'POPIA & GDPR', description: 'Full compliance with SA and EU data protection.' },
      { icon: '🚀', title: 'Rapid Delivery', description: '2-week sprint cycles, CI/CD pipelines.' },
      { icon: '🗣️', title: 'English Native', description: 'Perfect English communication, no barriers.' },
      { icon: '📈', title: 'Scalable Teams', description: 'From 2 to 50+ developers in weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore outsourcing compare to Johannesburg agencies?', answer: 'You get the same or better quality at 60% lower cost, with timezone proximity and English-native teams.' },
      { question: 'Do you comply with POPIA?', answer: 'Yes. We are fully POPIA and GDPR compliant with ISO 27001 certification.' },
    ],
    localRate: 'ZAR 800-1,500/h',
    ourRate: 'ZAR 300-600/h',
    savings: '60%',
  },

  'nairobi': {
    slug: 'software-development-agency-nairobi',
    cityName: 'Nairobi',
    countryName: 'Kenya',
    flag: '🇰🇪',
    timezone: 'EAT (GMT+3)',
    currency: 'KES',
    title: {
      en: 'Software Development Agency Nairobi | Call IT Dev',
      fr: 'Agence de Développement Logiciel Nairobi | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات نيروبي | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Nairobi | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Nairobi businesses. React, AI, mobile — 55% savings. Free consultation.',
      fr: 'Agence de développement logiciel pour Nairobi. React, IA, mobile — 55% d\'économies. Consultation gratuite.',
      ar: 'وكالة تطوير برمجيات رائدة لشركات نيروبي. React، ذكاء اصطناعي، موبايل — وفر 55%.',
      es: 'Agencia líder de desarrollo de software para Nairobi. React, IA, móvil — 55% ahorro.',
    },
    keywords: ['software development Nairobi', 'web development Kenya', 'app development Nairobi', 'React developers Kenya'],
    heroTitle: {
      en: 'Software Development Agency in Nairobi',
      fr: 'Agence de Développement Logiciel à Nairobi',
      ar: 'وكالة تطوير البرمجيات في نيروبي',
      es: 'Agencia de Desarrollo de Software en Nairobi',
    },
    heroSubtitle: {
      en: 'Enterprise software for East Africa\'s tech hub. React, AI, cloud — 55% savings vs Nairobi rates.',
      fr: 'Logiciels d\'entreprise pour le hub tech d\'Afrique de l\'Est. React, IA, cloud — 55% d\'économies.',
      ar: 'برمجيات مؤسسية لمركز التكنولوجيا في شرق أفريقيا. React، ذكاء اصطناعي — وفر 55%.',
      es: 'Software empresarial para el hub tech de África Oriental. React, IA — 55% ahorro.',
    },
    whyTitle: {
      en: 'Why Nairobi Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Nairobi Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات نيروبي Call IT Dev',
      es: 'Por qué las Empresas de Nairobi Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '💰', title: '55% Cost Savings', description: 'High-quality development at nearshore prices.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+3 — 2 hours from Morocco.' },
      { icon: '🗣️', title: 'English + French', description: 'Bilingual teams for East African markets.' },
      { icon: '🚀', title: 'Fintech Expertise', description: 'Deep experience with M-Pesa, mobile money.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Kenya DPA and GDPR compliant.' },
      { icon: '📈', title: 'Scale Fast', description: 'Ramp teams in 2-3 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have experience with fintech for East Africa?', answer: 'Yes. We have built mobile money integrations, M-Pesa APIs, and fintech platforms for the East African market.' },
    ],
    localRate: 'KES 8,000-15,000/h',
    ourRate: 'KES 3,500-7,000/h',
    savings: '55%',
  },

  'manila': {
    slug: 'software-development-agency-manila',
    cityName: 'Manila',
    countryName: 'Philippines',
    flag: '🇵🇭',
    timezone: 'PHT (GMT+8)',
    currency: 'PHP',
    title: {
      en: 'Software Development Agency Manila | Call IT Dev',
      fr: 'Agence de Développement Logiciel Manille | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Manila | Call IT Dev',
      ja: 'ソフトウェア開発エージェンシー マニラ | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Manila businesses. React, AI, cloud — enterprise quality at competitive rates.',
      fr: 'Agence de développement logiciel pour Manille. React, IA, cloud — qualité entreprise à prix compétitifs.',
      es: 'Agencia líder de desarrollo de software para Manila. React, IA, nube — calidad empresarial.',
      ja: 'マニラのトップソフトウェア開発エージェンシー。React、AI、クラウド — 競争力のある価格で企業品質。',
    },
    keywords: ['software development Manila', 'web development Philippines', 'app development Manila', 'React developers Philippines'],
    heroTitle: {
      en: 'Software Development Agency in Manila',
      fr: 'Agence de Développement Logiciel à Manille',
      es: 'Agencia de Desarrollo de Software en Manila',
      ja: 'マニラのソフトウェア開発エージェンシー',
    },
    heroSubtitle: {
      en: 'Enterprise software for Philippine businesses. React, AI, cloud — European quality, competitive rates.',
      fr: 'Logiciels d\'entreprise pour les Philippines. React, IA, cloud — qualité européenne.',
      es: 'Software empresarial para Filipinas. React, IA, nube — calidad europea.',
      ja: 'フィリピン企業向けエンタープライズソフトウェア。React、AI、クラウド。',
    },
    whyTitle: {
      en: 'Why Manila Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Manille Choisissent Call IT Dev',
      es: 'Por qué las Empresas de Manila Eligen Call IT Dev',
      ja: 'マニラの企業がCall IT Devを選ぶ理由',
    },
    whyReasons: [
      { icon: '🌍', title: 'European Access', description: 'Native EU language support for global expansion.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'GMT+1 complements GMT+8 for 24/7 coverage.' },
      { icon: '🗣️', title: 'English Native', description: 'Perfect English communication.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'EU-compliant for European expansion.' },
      { icon: '📈', title: 'Scale', description: '500+ developers available.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why would a Philippine company use a Morocco-based agency?', answer: 'For European market access with native EU language capabilities, GDPR compliance, and follow-the-sun development coverage.' },
    ],
    localRate: 'PHP 2,500-5,000/h',
    ourRate: 'PHP 1,200-2,500/h',
    savings: '50%',
  },

  'ho-chi-minh': {
    slug: 'software-development-agency-ho-chi-minh',
    cityName: 'Ho Chi Minh City',
    countryName: 'Vietnam',
    flag: '🇻🇳',
    timezone: 'ICT (GMT+7)',
    currency: 'VND',
    title: {
      en: 'Software Development Agency Ho Chi Minh City | Call IT Dev',
      fr: 'Agence de Développement Logiciel Hô-Chi-Minh-Ville | Call IT Dev',
      ja: 'ソフトウェア開発エージェンシー ホーチミン | Call IT Dev',
      ko: '소프트웨어 개발 에이전시 호치민 | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Ho Chi Minh City. React, AI, cloud — European quality at competitive rates.',
      fr: 'Agence de développement logiciel pour Hô-Chi-Minh-Ville. React, IA, cloud — qualité européenne.',
      ja: 'ホーチミンのトップソフトウェア開発エージェンシー。React、AI、クラウド。',
      ko: '호치민 최고의 소프트웨어 개발 에이전시. React, AI, 클라우드.',
    },
    keywords: ['software development Ho Chi Minh', 'web development Vietnam', 'app development HCMC', 'React developers Vietnam'],
    heroTitle: {
      en: 'Software Development Agency in Ho Chi Minh City',
      fr: 'Agence de Développement Logiciel à Hô-Chi-Minh-Ville',
      ja: 'ホーチミンのソフトウェア開発エージェンシー',
      ko: '호치민의 소프트웨어 개발 에이전시',
    },
    heroSubtitle: {
      en: 'Enterprise software for Vietnamese businesses expanding globally. React, AI, cloud — European expertise.',
      fr: 'Logiciels d\'entreprise pour les entreprises vietnamiennes. React, IA, cloud — expertise européenne.',
      ja: 'グローバル展開するベトナム企業向けエンタープライズソフトウェア。',
      ko: '글로벌 확장하는 베트남 기업을 위한 엔터프라이즈 소프트웨어.',
    },
    whyTitle: {
      en: 'Why Ho Chi Minh City Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Hô-Chi-Minh-Ville Choisissent Call IT Dev',
      ja: 'ホーチミンの企業がCall IT Devを選ぶ理由',
      ko: '호치민 기업이 Call IT Dev를 선택하는 이유',
    },
    whyReasons: [
      { icon: '🌍', title: 'EU Market Access', description: 'Native European languages for global expansion.' },
      { icon: '💰', title: 'Competitive Pricing', description: 'Enterprise quality, nearshore rates.' },
      { icon: '⏰', title: '24/7 Coverage', description: 'GMT+1 complements GMT+7 perfectly.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'EU data protection ready.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Agile sprints, 2-week cycles.' },
      { icon: '📈', title: 'Scalable', description: 'Teams from 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone difference work?', answer: 'GMT+1 and GMT+7 create a natural follow-the-sun model, enabling 24/7 development coverage with overlapping hours for collaboration.' },
    ],
    localRate: '$15-30/h',
    ourRate: '$8-18/h',
    savings: '45%',
  },

  'bogota': {
    slug: 'software-development-agency-bogota',
    cityName: 'Bogotá',
    countryName: 'Colombia',
    flag: '🇨🇴',
    timezone: 'COT (GMT-5)',
    currency: 'COP',
    title: {
      en: 'Software Development Agency Bogotá | Call IT Dev',
      fr: 'Agence de Développement Logiciel Bogotá | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Bogotá | Call IT Dev',
      pt: 'Agência de Desenvolvimento de Software Bogotá | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Bogotá. React, AI, cloud — 55% savings vs local rates. Free consultation.',
      fr: 'Agence de développement logiciel à Bogotá. React, IA, cloud — 55% d\'économies.',
      es: 'Agencia líder de desarrollo de software en Bogotá. React, IA, nube — 55% ahorro vs tarifas locales.',
      pt: 'Agência líder de desenvolvimento de software em Bogotá. React, IA, nuvem — 55% economia.',
    },
    keywords: ['software development Bogota', 'desarrollo software Bogota', 'web development Colombia', 'React developers Bogota'],
    heroTitle: {
      en: 'Software Development Agency in Bogotá',
      fr: 'Agence de Développement Logiciel à Bogotá',
      es: 'Agencia de Desarrollo de Software en Bogotá',
      pt: 'Agência de Desenvolvimento de Software em Bogotá',
    },
    heroSubtitle: {
      en: 'Enterprise software for Colombian businesses. React, AI, cloud — European quality at 55% savings.',
      fr: 'Logiciels d\'entreprise pour la Colombie. React, IA, cloud — qualité européenne, 55% d\'économies.',
      es: 'Software empresarial para Colombia. React, IA, nube — calidad europea con 55% de ahorro.',
      pt: 'Software empresarial para a Colômbia. React, IA, nuvem — qualidade europeia com 55% de economia.',
    },
    whyTitle: {
      en: 'Why Bogotá Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bogotá Choisissent Call IT Dev',
      es: 'Por qué las Empresas de Bogotá Eligen Call IT Dev',
      pt: 'Por que Empresas de Bogotá Escolhem a Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'European Languages', description: 'Native French, German, Arabic for EU/MENA expansion.' },
      { icon: '💰', title: '55% Savings', description: 'Compared to Bogotá development rates.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'EU-compliant for European market entry.' },
      { icon: '🗣️', title: 'Spanish Native', description: 'Perfect Spanish + 12 other languages.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Agile sprints, 2-week cycles.' },
      { icon: '📈', title: 'Scale', description: 'Teams from 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you speak Spanish?', answer: 'Yes! We have native Spanish speakers along with 12+ other European and MENA languages.' },
    ],
    localRate: 'COP 180,000-350,000/h',
    ourRate: 'COP 80,000-160,000/h',
    savings: '55%',
  },

  'lima': {
    slug: 'software-development-agency-lima',
    cityName: 'Lima',
    countryName: 'Peru',
    flag: '🇵🇪',
    timezone: 'PET (GMT-5)',
    currency: 'PEN',
    title: {
      en: 'Software Development Agency Lima | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Lima | Call IT Dev',
      fr: 'Agence de Développement Logiciel Lima | Call IT Dev',
      pt: 'Agência de Desenvolvimento de Software Lima | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Lima businesses. React, AI, cloud — 50% savings vs local rates.',
      es: 'Agencia líder de desarrollo de software en Lima. React, IA, nube — 50% ahorro vs tarifas locales.',
      fr: 'Agence de développement logiciel à Lima. React, IA, cloud — 50% d\'économies.',
      pt: 'Agência de desenvolvimento de software em Lima. React, IA, nuvem — 50% economia.',
    },
    keywords: ['software development Lima', 'desarrollo software Lima', 'web development Peru', 'React developers Lima'],
    heroTitle: {
      en: 'Software Development Agency in Lima',
      es: 'Agencia de Desarrollo de Software en Lima',
      fr: 'Agence de Développement Logiciel à Lima',
      pt: 'Agência de Desenvolvimento de Software em Lima',
    },
    heroSubtitle: {
      en: 'Enterprise software for Peruvian businesses. React, AI, cloud — European expertise at 50% savings.',
      es: 'Software empresarial para Perú. React, IA, nube — expertise europea con 50% de ahorro.',
      fr: 'Logiciels d\'entreprise pour le Pérou. React, IA, cloud — expertise européenne, 50% d\'économies.',
      pt: 'Software empresarial para o Peru. React, IA, nuvem — expertise europeia com 50% de economia.',
    },
    whyTitle: {
      en: 'Why Lima Companies Choose Call IT Dev',
      es: 'Por qué las Empresas de Lima Eligen Call IT Dev',
      fr: 'Pourquoi les Entreprises de Lima Choisissent Call IT Dev',
      pt: 'Por que Empresas de Lima Escolhem a Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'EU Expansion', description: 'Native European languages for global markets.' },
      { icon: '💰', title: '50% Savings', description: 'Compared to Lima development rates.' },
      { icon: '🗣️', title: 'Spanish + 12 Languages', description: 'Native Spanish and European languages.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'EU data protection for market entry.' },
      { icon: '🚀', title: 'Agile Delivery', description: '2-week sprint cycles.' },
      { icon: '📈', title: 'Scalable Teams', description: 'From MVP to enterprise scale.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you help Peruvian companies expand to Europe?', answer: 'Yes. We provide native EU language support, GDPR compliance, and European market expertise.' },
    ],
    localRate: 'PEN 100-200/h',
    ourRate: 'PEN 50-100/h',
    savings: '50%',
  },

  'doha': {
    slug: 'software-development-agency-doha',
    cityName: 'Doha',
    countryName: 'Qatar',
    flag: '🇶🇦',
    timezone: 'AST (GMT+3)',
    currency: 'QAR',
    title: {
      en: 'Software Development Agency Doha | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات الدوحة | Call IT Dev',
      fr: 'Agence de Développement Logiciel Doha | Call IT Dev',
      de: 'Softwareentwicklungsagentur Doha | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Doha. React, AI, cloud — 65% savings vs Qatar rates. Free consultation.',
      ar: 'وكالة تطوير برمجيات رائدة في الدوحة. React، ذكاء اصطناعي، سحابة — وفر 65%. استشارة مجانية.',
      fr: 'Agence de développement logiciel à Doha. React, IA, cloud — 65% d\'économies vs tarifs Qatar.',
      de: 'Führende Softwareentwicklungsagentur für Doha. React, KI, Cloud — 65% Ersparnis.',
    },
    keywords: ['software development Doha', 'web development Qatar', 'app development Doha', 'تطوير برمجيات الدوحة'],
    heroTitle: {
      en: 'Software Development Agency in Doha',
      ar: 'وكالة تطوير البرمجيات في الدوحة',
      fr: 'Agence de Développement Logiciel à Doha',
      de: 'Softwareentwicklungsagentur in Doha',
    },
    heroSubtitle: {
      en: 'Enterprise software for Qatar\'s vision 2030. React, AI, cloud — 65% savings vs Doha rates.',
      ar: 'برمجيات مؤسسية لرؤية قطر 2030. React، ذكاء اصطناعي — وفر 65%.',
      fr: 'Logiciels d\'entreprise pour la Vision 2030 du Qatar. React, IA — 65% d\'économies.',
      de: 'Enterprise-Software für Katars Vision 2030. React, KI — 65% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Doha Companies Choose Call IT Dev',
      ar: 'لماذا تختار شركات الدوحة Call IT Dev',
      fr: 'Pourquoi les Entreprises de Doha Choisissent Call IT Dev',
      de: 'Warum Unternehmen in Doha Call IT Dev wählen',
    },
    whyReasons: [
      { icon: '🕌', title: 'Arabic Native', description: 'Native Arabic developers and project managers.' },
      { icon: '💰', title: '65% Savings', description: 'Compared to QAR 600-1,200/h Doha rates.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+1 — 2 hours from Qatar.' },
      { icon: '🛡️', title: 'Qatar DPL', description: 'Compliant with Qatar data protection law.' },
      { icon: '🚀', title: 'Vision 2030', description: 'Digital transformation expertise.' },
      { icon: '🗣️', title: 'Multilingual', description: 'Arabic, French, English, and more.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Arabic-speaking developers?', answer: 'Yes. Our teams include native Arabic speakers for seamless collaboration with Qatari businesses.' },
    ],
    localRate: 'QAR 600-1,200/h',
    ourRate: 'QAR 200-450/h',
    savings: '65%',
  },

  'muscat': {
    slug: 'software-development-agency-muscat',
    cityName: 'Muscat',
    countryName: 'Oman',
    flag: '🇴🇲',
    timezone: 'GST (GMT+4)',
    currency: 'OMR',
    title: {
      en: 'Software Development Agency Muscat | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات مسقط | Call IT Dev',
      fr: 'Agence de Développement Logiciel Mascate | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Muscat. React, AI, cloud — 60% savings vs Oman rates. Free consultation.',
      ar: 'أفضل وكالة تطوير برمجيات في مسقط. React، ذكاء اصطناعي، سحابة — وفر 60%. استشارة مجانية.',
      fr: 'Agence de développement logiciel à Mascate. React, IA, cloud — 60% d\'économies.',
    },
    keywords: ['software development Muscat', 'web development Oman', 'app development Muscat', 'تطوير برمجيات مسقط'],
    heroTitle: {
      en: 'Software Development Agency in Muscat',
      ar: 'وكالة تطوير البرمجيات في مسقط',
      fr: 'Agence de Développement Logiciel à Mascate',
    },
    heroSubtitle: {
      en: 'Enterprise software for Oman\'s digital transformation. React, AI, cloud — 60% savings.',
      ar: 'برمجيات مؤسسية للتحول الرقمي في عمان. React، ذكاء اصطناعي — وفر 60%.',
      fr: 'Logiciels d\'entreprise pour la transformation digitale d\'Oman. 60% d\'économies.',
    },
    whyTitle: {
      en: 'Why Muscat Companies Choose Call IT Dev',
      ar: 'لماذا تختار شركات مسقط Call IT Dev',
      fr: 'Pourquoi les Entreprises de Mascate Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🕌', title: 'Arabic First', description: 'Native Arabic teams for Omani businesses.' },
      { icon: '💰', title: '60% Savings', description: 'Compared to Muscat development rates.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+1 — 3 hours from Oman.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Compliant with Oman cyber security law.' },
      { icon: '🚀', title: 'Vision 2040', description: 'Supporting Oman\'s digital agenda.' },
      { icon: '🗣️', title: 'Multilingual', description: 'Arabic, English, French.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore outsourcing benefit Omani companies?', answer: 'Morocco provides native Arabic development teams, cultural alignment, competitive rates, and proximity for Omani businesses.' },
    ],
    localRate: 'OMR 25-50/h',
    ourRate: 'OMR 10-20/h',
    savings: '60%',
  },

  'accra': {
    slug: 'software-development-agency-accra',
    cityName: 'Accra',
    countryName: 'Ghana',
    flag: '🇬🇭',
    timezone: 'GMT (GMT+0)',
    currency: 'GHS',
    title: {
      en: 'Software Development Agency Accra | Call IT Dev',
      fr: 'Agence de Développement Logiciel Accra | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Accra businesses. React, AI, cloud — enterprise quality at competitive rates.',
      fr: 'Agence de développement logiciel pour les entreprises d\'Accra. React, IA, cloud — qualité entreprise.',
    },
    keywords: ['software development Accra', 'web development Ghana', 'app development Accra', 'React developers Ghana'],
    heroTitle: {
      en: 'Software Development Agency in Accra',
      fr: 'Agence de Développement Logiciel à Accra',
    },
    heroSubtitle: {
      en: 'Enterprise software for West Africa\'s rising tech hub. React, AI, cloud — European quality.',
      fr: 'Logiciels d\'entreprise pour le hub tech d\'Afrique de l\'Ouest. React, IA, cloud.',
    },
    whyTitle: {
      en: 'Why Accra Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Accra Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'African Tech Hub', description: 'Supporting Ghana\'s growing tech ecosystem.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '⏰', title: 'Same Timezone', description: 'GMT+0 — only 1 hour from Morocco.' },
      { icon: '🗣️', title: 'English + French', description: 'Bilingual for West African markets.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Ghana DPA and GDPR aligned.' },
      { icon: '📈', title: 'Scale Fast', description: 'From MVP to enterprise in weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you serve Ghanaian fintech startups?', answer: 'Yes. We specialize in fintech, mobile money, and payment platforms for West African markets.' },
    ],
    localRate: 'GHS 150-300/h',
    ourRate: 'GHS 70-140/h',
    savings: '55%',
  },

  'tunis': {
    slug: 'software-development-agency-tunis',
    cityName: 'Tunis',
    countryName: 'Tunisia',
    flag: '🇹🇳',
    timezone: 'CET (GMT+1)',
    currency: 'TND',
    title: {
      en: 'Software Development Agency Tunis | Call IT Dev',
      fr: 'Agence de Développement Logiciel Tunis | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات تونس | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Tunis businesses. React, AI, cloud — same timezone, competitive rates.',
      fr: 'Agence de développement logiciel à Tunis. React, IA, cloud — même fuseau horaire, tarifs compétitifs.',
      ar: 'وكالة تطوير برمجيات رائدة في تونس. React، ذكاء اصطناعي، سحابة — نفس المنطقة الزمنية.',
    },
    keywords: ['software development Tunis', 'développement logiciel Tunis', 'web development Tunisia', 'تطوير برمجيات تونس'],
    heroTitle: {
      en: 'Software Development Agency in Tunis',
      fr: 'Agence de Développement Logiciel à Tunis',
      ar: 'وكالة تطوير البرمجيات في تونس',
    },
    heroSubtitle: {
      en: 'Enterprise software for Tunisian businesses. React, AI, cloud — same CET timezone, competitive rates.',
      fr: 'Logiciels d\'entreprise pour les entreprises tunisiennes. React, IA, cloud — même fuseau CET.',
      ar: 'برمجيات مؤسسية للشركات التونسية. React، ذكاء اصطناعي — نفس المنطقة الزمنية.',
    },
    whyTitle: {
      en: 'Why Tunis Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Tunis Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات تونس Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Maghreb Partner', description: 'Cultural and linguistic alignment.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality, nearshore prices.' },
      { icon: '⏰', title: 'Same Timezone', description: 'CET (GMT+1) — zero time difference.' },
      { icon: '🗣️', title: 'Arabic + French', description: 'Native bilingual teams.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Tunisian DPL and GDPR aligned.' },
      { icon: '📈', title: 'Scale', description: 'Ramp teams quickly for any project.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why choose a Morocco-based agency over local Tunisian firms?', answer: 'We offer larger talent pools, ISO certifications, and proven enterprise experience while sharing the same timezone, language, and cultural context.' },
    ],
    localRate: 'TND 80-160/h',
    ourRate: 'TND 40-80/h',
    savings: '50%',
  },
};
